import {
  IUserData,
  IWorkflowFormFieldData,
  IWorkflowFormFieldOptionData,
  IWorkflowFormFieldOptionTodoData,
  IWorkflowFormFieldOptionTodoTemplateData,
  IWorkflowFormTemplateData,
  IWorkflowFormTemplateRevisionData,
  IWorkflowStateData
} from '@/models';
import { AxiosInstance } from 'axios';
import {
  IFetchWorkflowFormFieldOptionTodosParams,
  IFetchWorkflowFormFieldParams,
  IFetchWorkflowFormTemplatesParams,
  IWorkflowFormTemplateService,
  IUpdateWorkflowFormTemplateRevisionStatePayload,
  IFetchWorkflowFormTemplateRevisionsParams
} from './workflow-form-template.types';
import { objectToFormData } from '@/utils/objectToFormData';
import { UPLOAD_WORKFLOW_FILE_FIELD_TIMEOUT_IN_MS } from './workflow-form-template.constants';
import { IAttachmentData } from '@/models/attachment/attachment.types';

export class WorkflowFormTemplateService
  implements IWorkflowFormTemplateService {
  private endpoint: string;

  constructor(private axios: AxiosInstance) {
    this.endpoint = 'v1/user-apps/';
  }

  fetchWorkflowFormTemplates(
    params?: IFetchWorkflowFormTemplatesParams
  ): Promise<IWorkflowFormTemplateData[]> {
    return this.axios
      .get<IWorkflowFormTemplateData[]>(`${this.endpoint}templates/`, {
        params: params
      })
      .then(({ data }) => data);
  }

  fetchWorkflowFormTemplate(id: number): Promise<IWorkflowFormTemplateData> {
    return this.axios
      .get<IWorkflowFormTemplateData>(`${this.endpoint}templates/${id}/`)
      .then(({ data }) => data);
  }

  createWorkflowFormTemplate(
    data: IWorkflowFormTemplateData
  ): Promise<IWorkflowFormTemplateData> {
    return this.axios
      .post<IWorkflowFormTemplateData>(`${this.endpoint}templates/`, data)
      .then(({ data }) => data);
  }

  updateWorkflowFormTemplate(
    data: IWorkflowFormTemplateData
  ): Promise<IWorkflowFormTemplateData> {
    return this.axios
      .patch<IWorkflowFormTemplateData>(
        `${this.endpoint}templates/${data.id}/`,
        data
      )
      .then(({ data }) => data);
  }

  deleteWorkflowFormTemplate(id: number): Promise<any> {
    return this.axios.delete(`${this.endpoint}templates/${id}/`);
  }

  /** ### REVISIONS ### */
  fetchWorkflowFormTemplatesRevisions(
    params?: IFetchWorkflowFormTemplateRevisionsParams
  ): Promise<IWorkflowFormTemplateRevisionData[]> {
    return this.axios
      .get<IWorkflowFormTemplateRevisionData[]>(
        `${this.endpoint}template-revisions/`,
        {
          params: params
        }
      )
      .then(({ data }) => data);
  }

  fetchWorkflowFormTemplateRevisions(
    templateId: number,
    params?: IFetchWorkflowFormTemplateRevisionsParams
  ): Promise<IWorkflowFormTemplateRevisionData[]> {
    return this.axios
      .get<IWorkflowFormTemplateRevisionData[]>(
        `${this.endpoint}templates/${templateId}/revisions/`,
        {
          params: params
        }
      )
      .then(({ data }) => data);
  }

  fetchWorkflowFormTemplateRevision(
    templateId: number,
    revisionId: number
  ): Promise<IWorkflowFormTemplateRevisionData> {
    return this.axios
      .get<IWorkflowFormTemplateRevisionData>(
        `${this.endpoint}templates/${templateId}/revisions/${revisionId}/`
      )
      .then(({ data }) => data);
  }

  createWorkflowFormTemplateRevision(
    templateId: number,
    data: IWorkflowFormTemplateRevisionData
  ): Promise<IWorkflowFormTemplateRevisionData> {
    return this.axios
      .post<IWorkflowFormTemplateRevisionData>(
        `${this.endpoint}templates/${templateId}/revisions/`,
        data
      )
      .then(({ data }) => data);
  }
  updateWorkflowFormTemplateRevision(
    templateId: number,
    data: IWorkflowFormTemplateRevisionData
  ): Promise<IWorkflowFormTemplateRevisionData> {
    return this.axios
      .patch<IWorkflowFormTemplateRevisionData>(
        `${this.endpoint}templates/${templateId}/revisions/${data.id}/`,
        data
      )
      .then(({ data }) => data);
  }

  deleteWorkflowFormTemplateRevision(
    templateId: number,
    revisionId: number
  ): Promise<any> {
    return this.axios.delete(
      `${this.endpoint}templates/${templateId}/revisions/${revisionId}/`
    );
  }

  addWorkflowFormTemplateAttachment(
    templateId: number,
    attachment: IAttachmentData
  ): Promise<IAttachmentData> {
    const formData = objectToFormData({ attachment });
    return this.axios
      .post<IAttachmentData>(
        `${this.endpoint}templates/${templateId}/add-attachments/`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      )
      .then(({ data }) => data);
  }

  fetchWorkflowFormTemplateRevisionsStakeholderUsers() {
    return this.axios
      .get<IUserData[]>(`${this.endpoint}templates/stakeholder-users/`)
      .then(({ data }) => data);
  }

  removeWorkflowFormTemplateAttachment(
    templateId: number,
    attachmentId: number
  ): Promise<void> {
    return this.axios
      .delete(
        `${this.endpoint}templates/${templateId}/remove-attachment/${attachmentId}/`
      )
      .then(({ data }) => data);
  }

  downloadWorkflowFormTemplateAttachment(
    templateId: number,
    revisionId: number,
    attachmentId: number
  ) {
    return this.axios
      .get<Blob>(
        `${this.endpoint}templates/${templateId}/revisions/${revisionId}/download-attachment/${attachmentId}/`,
        {
          responseType: 'blob'
        }
      )
      .then(({ data }) => data);
  }

  createNewWorkflowTemplateRevision(
    templateId: number,
    revisionId: number
  ): Promise<IWorkflowFormTemplateRevisionData> {
    return this.axios
      .post<IWorkflowFormTemplateRevisionData>(
        `${this.endpoint}templates/${templateId}/revisions/${revisionId}/create-revision/`
      )
      .then(({ data }) => data);
  }

  updateWorkflowFormTemplateRevisionState(
    data: IUpdateWorkflowFormTemplateRevisionStatePayload
  ): Promise<IWorkflowFormTemplateRevisionData> {
    return this.axios
      .put<IWorkflowFormTemplateRevisionData>(
        `${this.endpoint}templates/${data.templateId}/revisions/${data.revisionId}/set-state/${data.state}/`
      )
      .then(({ data }) => data);
  }

  fetchTemplateWorkflowStates(
    templateId: number
  ): Promise<IWorkflowStateData[]> {
    return this.axios
      .get<IWorkflowStateData[]>(
        `${this.endpoint}templates/${templateId}/workflow-states/`
      )
      .then(({ data }) => data);
  }

  fetchWorkflowTemplateUsers(
    templateId: number
  ): Promise<IUserData[]> {
    return this.axios
      .get<IUserData[]>(
        `${this.endpoint}templates/${templateId}/users/`
      )
      .then(({ data }) => data);
  }

  //fields
  fetchWorkflowFormFields(
    params: IFetchWorkflowFormFieldParams
  ): Promise<IWorkflowFormFieldData[]> {
    return this.axios
      .get<IWorkflowFormFieldData[]>(`${this.endpoint}fields/`, {
        params: params
      })
      .then(({ data }) => data);
  }

  fetchWorkflowFormField(id: number): Promise<IWorkflowFormFieldData> {
    return this.axios
      .get<IWorkflowFormFieldData>(`${this.endpoint}fields/${id}/`)
      .then(({ data }) => data);
  }

  createWorkflowFormField(
    data: IWorkflowFormFieldData
  ): Promise<IWorkflowFormFieldData> {
    return this.axios
      .post<IWorkflowFormFieldData>(`${this.endpoint}fields/`, data)
      .then(({ data }) => data);
  }

  updateWorkflowFormField(
    data: IWorkflowFormFieldData
  ): Promise<IWorkflowFormFieldData> {
    return this.axios
      .patch<IWorkflowFormFieldData>(`${this.endpoint}fields/${data.id}/`, data)
      .then(({ data }) => data);
  }

  deleteWorkflowFormField(id: number): Promise<any> {
    return this.axios.delete(`${this.endpoint}fields/${id}/`);
  }

  uploadWorkflowFormFieldOptions(
    id: number,
    file: File
  ): Promise<IWorkflowFormFieldOptionData[]> {
    const formData = objectToFormData({ file: file });
    return this.axios
      .post(`${this.endpoint}fields/${id}/upload-options/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: UPLOAD_WORKFLOW_FILE_FIELD_TIMEOUT_IN_MS
      })
      .then(({ data }) => data);
  }

  //todos
  fetchWorkflowFormFieldOptionTodos(
    params: IFetchWorkflowFormFieldOptionTodosParams
  ): Promise<IWorkflowFormFieldOptionTodoData[]> {
    return this.axios
      .get<IWorkflowFormFieldOptionTodoData[]>(
        `${this.endpoint}templates/${params.templateId}/revisions/${params.revisionId}/todos/`,
        {
          params: {
            section_field: params.sectionFieldId,
            field_option: params.fieldOptionId
          }
        }
      )
      .then(({ data }) => data);
  }

  createWorkflowFormFieldOptionTodo(
    params: IFetchWorkflowFormFieldOptionTodosParams,
    data: IWorkflowFormFieldOptionTodoData
  ): Promise<IWorkflowFormFieldOptionTodoData> {
    return this.axios
      .post<IWorkflowFormFieldOptionTodoData>(
        `${this.endpoint}templates/${params.templateId}/revisions/${params.revisionId}/todos/`,
        data
      )
      .then(({ data }) => data);
  }

  updateWorkflowFormFieldOptionTodo(
    params: IFetchWorkflowFormFieldOptionTodosParams,
    data: IWorkflowFormFieldOptionTodoData
  ): Promise<IWorkflowFormFieldOptionTodoData> {
    return this.axios
      .patch<IWorkflowFormFieldOptionTodoData>(
        `${this.endpoint}templates/${params.templateId}/revisions/${params.revisionId}/todos/${data.id}/`,
        data
      )
      .then(({ data }) => data);
  }

  deleteWorkflowFormFieldOptionTodo(
    params: IFetchWorkflowFormFieldOptionTodosParams,
    id: number
  ): Promise<any> {
    return this.axios.delete(
      `${this.endpoint}templates/${params.templateId}/revisions/${params.revisionId}/todos/${id}/`
    );
  }

  //todo template
  fetchWorkflowFormTodoTemplates(): Promise<
    IWorkflowFormFieldOptionTodoTemplateData[]
  > {
    return this.axios
      .get<IWorkflowFormFieldOptionTodoTemplateData[]>(
        `${this.endpoint}todo-templates/`
      )
      .then(({ data }) => data);
  }
  fetchWorkflowFormTodoTemplate(
    id: number
  ): Promise<IWorkflowFormFieldOptionTodoTemplateData> {
    return this.axios
      .get<IWorkflowFormFieldOptionTodoTemplateData>(
        `${this.endpoint}todo-templates/${id}/`
      )
      .then(({ data }) => data);
  }
  createWorkflowFormTodoTemplate(
    data: IWorkflowFormFieldOptionTodoTemplateData
  ): Promise<IWorkflowFormFieldOptionTodoTemplateData> {
    return this.axios
      .post<IWorkflowFormFieldOptionTodoTemplateData>(
        `${this.endpoint}todo-templates/`,
        data
      )
      .then(({ data }) => data);
  }
  updateWorkflowFormTodoTemplate(
    data: IWorkflowFormFieldOptionTodoTemplateData
  ): Promise<IWorkflowFormFieldOptionTodoTemplateData> {
    return this.axios
      .patch<IWorkflowFormFieldOptionTodoTemplateData>(
        `${this.endpoint}todo-templates/${data.id}/`,
        data
      )
      .then(({ data }) => data);
  }
  deleteWorkflowFormTodoTemplate(id: number): Promise<any> {
    return this.axios.delete(`${this.endpoint}todo-templates/${id}/`);
  }
}
